import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getBuilderStats,
  getOverview,
  type BuilderStat,
} from "@/api/admin";

type Period = "week" | "month" | "all_time";

export function AdminDashboard() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [builderPeriod, setBuilderPeriod] = useState<Period>("week");

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setCredentials({ username, password });
  };

  const {
    data: overview,
    isLoading: overviewLoading,
    error: overviewError,
  } = useQuery({
    queryKey: ["admin", "overview", credentials],
    queryFn: () => getOverview(credentials!.username, credentials!.password),
    enabled: !!credentials,
    retry: false,
  });

  const { data: builderStats, isLoading: buildersLoading } = useQuery({
    queryKey: ["admin", "builders", credentials],
    queryFn: () =>
      getBuilderStats(credentials!.username, credentials!.password),
    enabled: !!credentials,
    retry: false,
  });

  if (!credentials) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Admin Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full">
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (overviewError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-sm">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Invalid credentials</p>
            <Button
              variant="ghost"
              className="mt-4"
              onClick={() => setCredentials(null)}
            >
              Try again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (overviewLoading || !overview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const currentBuilders: BuilderStat[] =
    builderStats?.[builderPeriod] ?? [];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <span className="text-lg font-semibold">Admin Dashboard</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCredentials(null)}
          >
            Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 space-y-8">
        {/* Overview stats */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard label="Total Users" value={overview.total_users} />
          <StatCard label="Homeowners" value={overview.homeowners} />
          <StatCard label="Builders" value={overview.builders} />
          <StatCard label="Total Surveys" value={overview.total_surveys} />
          <StatCard
            label="Surveys (7d)"
            value={overview.surveys_last_7_days}
          />
          <StatCard
            label="Surveys (30d)"
            value={overview.surveys_last_30_days}
          />
          <StatCard
            label="Active Subscriptions"
            value={overview.active_subscriptions}
          />
          <StatCard
            label="Completed Orders"
            value={overview.completed_orders}
          />
        </div>

        {/* Top builders */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Top Builders by Surveys</CardTitle>
              <div className="flex gap-1">
                {(["week", "month", "all_time"] as const).map((p) => (
                  <Button
                    key={p}
                    variant={builderPeriod === p ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setBuilderPeriod(p)}
                  >
                    {p === "all_time" ? "All Time" : p === "week" ? "Week" : "Month"}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {buildersLoading ? (
              <p className="text-center text-muted-foreground">Loading...</p>
            ) : currentBuilders.length === 0 ? (
              <p className="text-center text-muted-foreground">
                No data for this period
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">#</th>
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">Company</th>
                    <th className="pb-2 text-right font-medium">Surveys</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBuilders.map((b, i) => (
                    <tr key={b.user_id} className="border-b last:border-0">
                      <td className="py-2">{i + 1}</td>
                      <td className="py-2">{b.email}</td>
                      <td className="py-2 text-muted-foreground">
                        {b.company_name || "-"}
                      </td>
                      <td className="py-2 text-right font-medium">
                        {b.survey_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Activity charts */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Surveys per Day (30d)</CardTitle>
            </CardHeader>
            <CardContent>
              <MiniBarChart data={overview.surveys_per_day} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Registrations per Day (30d)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MiniBarChart data={overview.registrations_per_day} />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

function MiniBarChart({ data }: { data: { date: string; count: number }[] }) {
  if (data.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground">No data</p>
    );
  }

  const max = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="flex items-end gap-0.5" style={{ height: 80 }}>
      {data.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${d.count}`}
          className="flex-1 rounded-t bg-primary/70 transition-all hover:bg-primary"
          style={{
            height: `${(d.count / max) * 100}%`,
            minHeight: d.count > 0 ? 2 : 0,
          }}
        />
      ))}
    </div>
  );
}
